import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  CallingService,
  CallingType,
  OutboundCallSummary,
  normalizeCallingType,
} from "@/integrations/api/services/callingService";
import { CommentsService, COMMENT_TYPES } from "@/integrations/api/services/commentsService";
import { toast } from "@/hooks/use-toast";

type CallPhase = "idle" | "forwarding" | "summary";

interface StartCallPayload {
  name: string;
  displayName?: string;
  loanApplicationId: number;
  loanId?: number;
  repaymentId: number;
  contactType: number;
  onCommentSaved?: () => void;
}

interface CallSessionContextValue {
  phase: CallPhase;
  minimized: boolean;
  loading: boolean;
  displayName: string;
  summary: OutboundCallSummary | null;
  commentText: string;
  selectedReason: string;
  callingPtpDate: string;
  /** UI Pre/Post mode from outbound calling_type ("pre" | "post"); null API → defaults to "post" */
  emiMode: CallingType;
  /** True when API returned "pre" | "post"; false when null (user may toggle) */
  isEmiModeLocked: boolean;
  startTeleCMICall: (payload: StartCallPayload) => Promise<void>;
  setMinimized: (value: boolean) => void;
  setCommentText: (value: string) => void;
  setSelectedReason: (value: string) => void;
  setCallingPtpDate: (value: string) => void;
  setEmiMode: (value: CallingType) => void;
  saveSummary: () => Promise<void>;
  endSession: () => void;
}

const CallSessionContext = createContext<CallSessionContextValue | null>(null);

export const CallSessionProvider = ({ children }: { children: ReactNode }) => {
  const [phase, setPhase] = useState<CallPhase>("idle");
  const [minimized, setMinimized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [summary, setSummary] = useState<OutboundCallSummary | null>(null);
  const [commentText, setCommentText] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [callingPtpDate, setCallingPtpDate] = useState("");
  const [outboundId, setOutboundId] = useState<number | null>(null);
  const [repaymentId, setRepaymentId] = useState<number | null>(null);
  const [loanId, setLoanId] = useState<number | null>(null);
  const [emiMode, setEmiMode] = useState<CallingType>("post");
  const [isEmiModeLocked, setIsEmiModeLocked] = useState(false);

  const pollTimerRef = useRef<number | null>(null);
  const onCommentSavedRef = useRef<(() => void) | undefined>(undefined);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const resetSession = useCallback(() => {
    clearPollTimer();
    setPhase("idle");
    setMinimized(false);
    setLoading(false);
    setDisplayName("");
    setSummary(null);
    setCommentText("");
    setSelectedReason("");
    setCallingPtpDate("");
    setOutboundId(null);
    setRepaymentId(null);
    setLoanId(null);
    setEmiMode("post");
    setIsEmiModeLocked(false);
    onCommentSavedRef.current = undefined;
  }, [clearPollTimer]);

  const startPollingSummary = useCallback((id: number) => {
    const poll = async () => {
      try {
        const fetchedSummary = await CallingService.getOutboundSummary(id);
        if (fetchedSummary?.call_start_time && fetchedSummary?.call_end_time) {
          const apiType = fetchedSummary.calling_type;
          const locked = apiType === "pre" || apiType === "post";
          setSummary(fetchedSummary);
          setEmiMode(normalizeCallingType(apiType));
          setIsEmiModeLocked(locked);
          setSelectedReason("");
          setCallingPtpDate("");
          setPhase("summary");
          setLoading(false);
          // Expand summary automatically if call ended while mini box was visible.
          setMinimized(false);
          return;
        }
        pollTimerRef.current = window.setTimeout(poll, 5000);
      } catch (error) {
        console.error("Error polling outbound summary:", error);
        toast({
          variant: "destructive",
          title: "Failed to fetch call summary",
          description: "Please try again.",
        });
        resetSession();
      }
    };

    pollTimerRef.current = window.setTimeout(poll, 5000);
  }, [resetSession]);

  const startTeleCMICall = useCallback(async (payload: StartCallPayload) => {
    try {
      clearPollTimer();
      setLoading(true);
      setSummary(null);
      setCommentText("");
      setSelectedReason("");
      setCallingPtpDate("");
      setEmiMode("post");
      setIsEmiModeLocked(false);
      setDisplayName(payload.displayName || payload.name);
      setRepaymentId(payload.repaymentId);
      setLoanId(payload.loanId ?? null);
      onCommentSavedRef.current = payload.onCommentSaved;
      setPhase("forwarding");
      setMinimized(true);

      const response = await CallingService.initiateCall({
        loan_application_id: Number(payload.loanApplicationId),
        repayment_id: Number(payload.repaymentId),
        contact_type: Number(payload.contactType),
      });

      if (!response.success || !response.data?.outbound_call_request_id) {
        throw new Error(response.error ?? "Failed to initiate call");
      }

      const id = response.data.outbound_call_request_id;
      setOutboundId(id);
      startPollingSummary(id);
    } catch (error: any) {
      console.error("Error initiating TeleCMI call:", error);
      toast({
        variant: "destructive",
        title: "Error initiating call",
        description: error?.message ?? "Something went wrong. Please try again.",
      });
      resetSession();
    }
  }, [clearPollTimer, resetSession, startPollingSummary]);

  const saveSummary = useCallback(async () => {
    if (!repaymentId || !outboundId) {
      toast({
        variant: "destructive",
        title: "Missing data",
        description: "Repayment or call id is missing.",
      });
      return;
    }
    if (!selectedReason) {
      toast({
        variant: "destructive",
        title: "Reason required",
        description: "Please select a reason before saving.",
      });
      return;
    }
    if (selectedReason === "PTP" && !callingPtpDate) {
      toast({
        variant: "destructive",
        title: "PTP date required",
        description: "Please select a PTP date before saving.",
      });
      return;
    }

    try {
      setLoading(true);
      await CommentsService.createComment({
        repayment_id: String(repaymentId),
        comment: commentText.trim() || null,
        comment_type: COMMENT_TYPES.APPLICATION_DETAILS,
        outbound_call_request_id: outboundId,
        loan_id: loanId ?? undefined,
        reason: selectedReason,
        Calling_ptp: callingPtpDate || undefined,
      });

      onCommentSavedRef.current?.();
      resetSession();
    } catch (error) {
      console.error("Failed to save call summary:", error);
      toast({
        variant: "destructive",
        title: "Failed to save",
        description: "Could not save call summary. Please try again.",
      });
      setLoading(false);
    }
  }, [callingPtpDate, commentText, loanId, outboundId, repaymentId, resetSession, selectedReason]);

  const endSession = useCallback(() => {
    resetSession();
  }, [resetSession]);

  const handleSetEmiMode = useCallback(
    (value: CallingType) => {
      if (isEmiModeLocked) return;
      setEmiMode(value);
      setSelectedReason("");
      setCallingPtpDate("");
    },
    [isEmiModeLocked]
  );

  useEffect(() => {
    return () => clearPollTimer();
  }, [clearPollTimer]);

  const value = useMemo<CallSessionContextValue>(
    () => ({
      phase,
      minimized,
      loading,
      displayName,
      summary,
      commentText,
      selectedReason,
      callingPtpDate,
      emiMode,
      isEmiModeLocked,
      startTeleCMICall,
      setMinimized,
      setCommentText,
      setSelectedReason,
      setCallingPtpDate,
      setEmiMode: handleSetEmiMode,
      saveSummary,
      endSession,
    }),
    [
      phase,
      minimized,
      loading,
      displayName,
      summary,
      commentText,
      selectedReason,
      callingPtpDate,
      emiMode,
      isEmiModeLocked,
      startTeleCMICall,
      handleSetEmiMode,
      saveSummary,
      endSession,
    ]
  );

  return <CallSessionContext.Provider value={value}>{children}</CallSessionContext.Provider>;
};

export const useCallSession = () => {
  const context = useContext(CallSessionContext);
  if (!context) {
    throw new Error("useCallSession must be used within CallSessionProvider");
  }
  return context;
};
